import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    FlowBlockId,
    FlowProperty,
    FlowValidationError,
    FlowValidationErrorType
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {VisualStreamIds} from '@mintdata-wsp/common/state/ui/visualDesigner/visualData/VisualStreamIds';
import {
    CloudProperty,
    PropertyType,
    PropertyWidgetName
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {
    FocusPropertyRequest,
    IDirtyProperties
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/FlowPropertiesPanelState';
import {SelectedBlockChangedEvent} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowEventsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action, IReactionDisposer, reaction} from 'mobx';

export class FlowPropertiesPanelController
{
    private _disposers:IReactionDisposer[] = [];

    public init():void
    {
        this._listenForBlockSelection();
        this._listenForFlowErrors();
    }

    public dispose():void
    {
        this._disposers.forEach(disposer => disposer());
    }

    public getDirtyProperties():IDirtyProperties
    {
        return this._getState().dirtyProperties;
    }

    @action
    public setDirtyProperty = (propKey:string) =>
    {
        this._getState().dirtyProperties[propKey] = true;
    };

    public getDirtyPropertyKey(blockId:FlowBlockId, propName:string):string
    {
        return `${blockId}_${propName}`;
    }

    public getNestedProperties(prop:FlowProperty):FlowProperty[]
    {
        const {meta, definition} = prop;
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        switch( widget )
        {
            case PropertyWidgetName.Map:
            {
                const items = meta && meta.items;
                if( !items ) return [];

                const {mapInfo} = definition;
                const {key: keyDefinition, value: valueDefinition} = mapInfo;

                const props = items.map(({key, value}, index) =>
                {
                    const keyProp = new FlowProperty(
                        keyDefinition.withName(this.getArrayPropertyName(keyDefinition.name, index))
                    );

                    const valueProp = new FlowProperty(
                        valueDefinition.withName(this.getArrayPropertyName(valueDefinition.name, index))
                    );

                    keyProp.value = key;
                    valueProp.value = value;

                    return [keyProp, valueProp];
                });

                return [].concat(...props);
            }

            case PropertyWidgetName.List:
            {
                const items = meta && meta.items;
                if( !items ) return [];

                const {listItem} = definition;

                const props = items.map((item, index) =>
                {
                    const prop = new FlowProperty(
                        listItem.withName(this.getArrayPropertyName(listItem.name, index))
                    );

                    prop.value = item;

                    return prop;
                });

                return [].concat(...props);
            }
        }

        return [];
    }

    public getArrayPropertyName(propName:string, index:number):string
    {
        return `${propName}_${index}`;
    }

    public getWidgetName(definition:CloudProperty):PropertyWidgetName | null
    {
        const {widget, type} = definition;

        if( widget )
        {
            return widget;
        }

        switch( type )
        {
            case PropertyType.String:
            {
                if( definition.isEnum )
                {
                    return PropertyWidgetName.Dropdown;
                }

                return PropertyWidgetName.TextInput;
            }
            case PropertyType.Number:
                return PropertyWidgetName.NumericInput;
            case PropertyType.Boolean:
                return PropertyWidgetName.Checkbox;
            case PropertyType.List:
                return PropertyWidgetName.List;
            case PropertyType.Map:
                return PropertyWidgetName.Map;
        }

        return null;
    }

    @action.bound
    public requestFocusProperty(blockId:FlowBlockId, propName:string):void
    {
        this._getState().focusProperty.request(new FocusPropertyRequest(blockId, propName));
    }

    public isPropertyToFocus(blockId:FlowBlockId, propName:string):boolean
    {
        const request = this._getState().focusProperty.getRequest();

        return request && request.blockId === blockId && request.propName === propName;
    }

    public getPropertyToFocus():FocusPropertyRequest | null
    {
        return this._getState().focusProperty.getRequest();
    }

    public setDoneFocusingProperty(animationDelayInMs:number = 0):void
    {
        this._getState().focusProperty.markRequestDone(animationDelayInMs);
    }

    private _listenForBlockSelection = ():void =>
    {
        this._onBlockSelectionChanged();

        controllers.ui.flowEvents.addListener(this._onBlockSelectionChanged);
    };

    private _listenForFlowErrors = () =>
    {
        const flow = controllers.ui.flow.getOpenFlow();

        const disposer = reaction(
            () => flow ? flow.flowErrors : [],
            (flowErrors) => this._onFlowErrorsChanged(flowErrors)
        );

        this._disposers.push(disposer);
    };

    private _onBlockSelectionChanged = (event?:SelectedBlockChangedEvent) =>
    {
        if( event && !event.manually )
        {
            return;
        }

        const vdController = controllers.ui.visualData;
        const selectedBlock = controllers.ui.flowSelection.getAnySingleSelectedBlock();

        if( selectedBlock )
        {
            const streams = VisualStreamIds.forBlock(selectedBlock);

            vdController.getOrCreateStreamer(streams.input);
        }
    };

    @action
    private _onFlowErrorsChanged = (errors:FlowValidationError[]) =>
    {
        if( errors )
        {
            const propertyErrors = errors.filter(error => error.type === FlowValidationErrorType.Property);

            if( propertyErrors.length > 0 )
            {
                propertyErrors.forEach(({flowBlockId, flowPropertyName}) =>
                {
                    const key = this.getDirtyPropertyKey(flowBlockId, flowPropertyName);

                    // todo: set isDirty for nested properties of complex widgets (MapWidget, ListWidget)
                    this.setDirtyProperty(key);
                });
            }
        }
    };

    private _getState()
    {
        return rootState.ui.visualDesigner.workbench.panels.flow.properties;
    }
}
